import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useUpdateMatingLabelsFreezeMutation as useGqlUpdateMatingLabelsFreezeMutation } from '@/app/services/freeze';
import {
  AddIntEmbsRequest,
  fertAndEmbryosOptions,
  FertAndEmbryosResponse,
  fertilizationListFreezeOptions,
  FertilizationListFreezeResponse,
  fetchFertAndEmbryos,
  MassInterimEmbryosRequest,
  PdfCreateFreezeRequest,
  UpdateApprovalNumberRequest,
  UpdateFreezeEmbryosRequest,
  UpdateFreezeTempEmbryosRequest,
  UpdateMatingLabelsRequest,
  UpdateODFreezeRequest,
  useAddIntEmbsMutation,
  useAddMassInterimEmbryosMutation,
  useApplyApprovalNumbersMutation,
  useCreateFreezePdfMutation,
  useDeactivateInterimEmbryosMutation,
  useUpdateEmbryoFieldMutation,
  useUpdateFertilizationFieldMutation,
  useUpdateFreezeEmbryosMutation,
  useUpdateFreezeTempEmbryosMutation,
  useUpdateInterimEmbryoFieldMutation,
  useUpdateODFreezeMutation,
  useValidateFreezeMutation,
  ValidateFreezeRequest,
} from '@/services/freeze';

export function useFertilizationListFreezeQuery(params: {
  fertilizationDate: string;
  fertilizationSiteId: string | null;
  searchQuery?: string;
}) {
  const enabled = !!params.fertilizationDate && !!params.fertilizationSiteId;

  const restQuery = useQuery({
    ...fertilizationListFreezeOptions({
      fertilizationDate: params.fertilizationDate,
      fertilizationSiteId: params.fertilizationSiteId!,
      searchQuery: params.searchQuery || null,
    }),
    enabled,
  });

  return {
    currentData: restQuery.data as
      | FertilizationListFreezeResponse[]
      | undefined,
    isLoading: restQuery.isFetching,
    refetch: () => {
      void restQuery.refetch();
    },
  };
}

export function useFertAndEmbryosFreezeQuery(params: {
  fertilizationDate: string;
  fertilizationSiteId: string | null;
  fertilizationIds: string[] | undefined;
}) {
  const enabled =
    !!params.fertilizationDate &&
    !!params.fertilizationSiteId &&
    !!params.fertilizationIds &&
    params.fertilizationIds.length > 0;

  const restQuery = useQuery({
    ...fertAndEmbryosOptions({
      fertilizationDate: params.fertilizationDate,
      fertilizationSiteId: params.fertilizationSiteId!,
      fertilizationIds: params.fertilizationIds || [],
    }),
    enabled,
  });

  return {
    data: restQuery.data as FertAndEmbryosResponse[] | undefined,
    isLoading: restQuery.isFetching,
    refetch: () => {
      void restQuery.refetch();
    },
  };
}

export function useLazyFertAndEmbryosFreezeQuery() {
  const trigger = async (params: {
    fertilizationDate: string | null;
    fertilizationSiteId: string | null;
    fertilizationIds: string[] | undefined;
  }) => {
    const result = await fetchFertAndEmbryos({
      fertilizationDate: params.fertilizationDate!,
      fertilizationSiteId: params.fertilizationSiteId!,
      fertilizationIds: params.fertilizationIds || [],
    });
    return result as FertAndEmbryosResponse[];
  };

  return trigger;
}

export function useUpdateFertTransactionFreeze() {
  const mutation = useUpdateFertilizationFieldMutation();

  const mutateAsync = async (params: {
    id: string;
    field: string;
    value: string | null;
  }) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateEmbryoTransaction() {
  const mutation = useUpdateEmbryoFieldMutation();

  const mutateAsync = async (params: {
    id: string;
    field: string;
    value: string | null;
  }) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateInterimEmbryoTransaction() {
  const mutation = useUpdateInterimEmbryoFieldMutation();

  const mutateAsync = async (params: {
    id: string;
    field: string;
    value: string | null;
  }) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateODFreeze() {
  const mutation = useUpdateODFreezeMutation();

  const mutateAsync = async (params: {
    input: UpdateODFreezeRequest;
    dtplus?: boolean;
  }) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isLoading: mutation.isPending,
  };
}

export function useAddIntEmbryos() {
  const mutation = useAddIntEmbsMutation();

  const mutateAsync = async (params: AddIntEmbsRequest) => {
    const result = await mutation.mutateAsync(params);
    return { addIntEmbs: result };
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useAddMassInterimEmbryos() {
  const mutation = useAddMassInterimEmbryosMutation();

  const mutateAsync = async (params: {
    input: MassInterimEmbryosRequest;
    dtplus?: boolean;
  }) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useDeactivateInterimEmbryo() {
  const mutation = useDeactivateInterimEmbryosMutation();

  const mutateAsync = async (params: { items: (string | null)[] }) => {
    const result = await mutation.mutateAsync(
      params.items.filter(Boolean) as string[],
    );
    return { deactivateInterimEmbryo: result };
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateFreezeEmbryosAsync() {
  const mutation = useUpdateFreezeEmbryosMutation();

  const mutateAsync = async (params: UpdateFreezeEmbryosRequest) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateFreezeTempEmbryosAsync() {
  const mutation = useUpdateFreezeTempEmbryosMutation();

  const mutateAsync = async (params: UpdateFreezeTempEmbryosRequest) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useValidateFreeze() {
  const mutation = useValidateFreezeMutation();

  const mutateAsync = async (params: ValidateFreezeRequest) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useApplyApprovalNumbersToEmbryos() {
  const mutation = useApplyApprovalNumbersMutation();

  const mutateAsync = async (params: {
    updates: UpdateApprovalNumberRequest[];
  }) => {
    return mutation.mutateAsync(params.updates);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useCreateFreezeValidationReport() {
  const mutation = useCreateFreezePdfMutation();

  const mutateAsync = async (params: { fertilizationIds: string[] }) => {
    const blob = await mutation.mutateAsync({
      items: [],
      filename: `freeze-validation-report-${new Date().toISOString()}.pdf`,
    });
    return { data: { createFreezeValidationReport: blob } };
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function usePdfCreateFreeze() {
  const mutation = useCreateFreezePdfMutation();

  const mutateAsync = async (params: PdfCreateFreezeRequest) => {
    return mutation.mutateAsync(params);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateMatingLabelsFreeze() {
  const [gqlMutate, { isLoading: gqlLoading }] =
    useGqlUpdateMatingLabelsFreezeMutation();

  const mutateAsync = async (params: UpdateMatingLabelsRequest) => {
    // todo: add rest endpoint for this
    return gqlMutate(params).unwrap();
  };

  return {
    mutateAsync,
    isPending: gqlLoading,
  };
}

export function useInvalidateFreezeQueries() {
  const queryClient = useQueryClient();

  return {
    invalidateFertilizationList: () => {
      void queryClient.invalidateQueries({
        queryKey: ['freeze', 'fertilizations'],
      });
    },
    invalidateFertAndEmbryos: () => {
      void queryClient.invalidateQueries({
        queryKey: ['freeze', 'fert-and-embryos'],
      });
    },
    invalidateAll: () => {
      void queryClient.invalidateQueries({ queryKey: ['freeze'] });
    },
  };
}
